import { motion } from "framer-motion";
import { Sparkles, Code, BarChart3, Megaphone, Zap, Globe, Smartphone } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";

const BrandifyServices = () => {
  const marketingServices = [
    {
      icon: <Megaphone className="w-8 h-8" />,
      title: "Brand Strategy & Identity",
      description: "Complete brand positioning, messaging, and visual identity systems that set you apart."
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Social Media Growth",
      description: "Organic and paid strategies to build engaged communities across all platforms."
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Performance Marketing",
      description: "Data-driven campaigns on Meta, Google, and TikTok optimized for maximum ROI."
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Influencer Marketing",
      description: "Strategic partnerships with creators who align with your brand values."
    }
  ];

  const aiServices = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "AI Content Creation",
      description: "Automated content generation for social media, blogs, and marketing materials."
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "AI Ad Optimization",
      description: "Machine learning algorithms that continuously improve your ad performance."
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Predictive Analytics",
      description: "AI-powered insights that forecast trends and identify growth opportunities."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Marketing Automation",
      description: "Intelligent workflows that nurture leads and convert customers 24/7."
    }
  ];

  const techServices = [
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Website Development",
      description: "High-performance websites built with modern frameworks and optimized for conversions."
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Mobile App Development",
      description: "Native and cross-platform apps that deliver exceptional user experiences."
    },
    {
      icon: <Code className="w-8 h-8" />,
      title: "Custom Software",
      description: "Tailored solutions for your unique business needs and workflows."
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "AI Integration",
      description: "Implement AI capabilities into your existing products and platforms."
    }
  ];

  return (
    <div className="min-h-screen bg-[#030303] brandify-body">
      <SEO brand="brandify" path="/brandify/services" title="Services" description="AI-powered brand strategy, growth marketing, paid media, and creative systems from Brandify." />
      <Navigation brand="brandify" />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="heading-font text-5xl md:text-7xl text-white mb-6" data-testid="services-heading">
              STRATEGY, CREATIVITY &
              <br />
              <span className="text-[#1cb5ae]">TECHNOLOGY</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Three pillars. One integrated approach. Everything your brand needs to dominate in the digital age.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Marketing Services */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl text-[#1cb5ae] mb-4" data-testid="marketing-section">
              MARKETING & GROWTH
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl">
              Strategic marketing that combines creative storytelling with data-driven execution.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {marketingServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-effect rounded-2xl p-8 hover:bg-white/10 transition-all"
              >
                <div className="w-16 h-16 rounded-full bg-[#1cb5ae]/20 flex items-center justify-center mb-4 text-[#1cb5ae]">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-semibold text-white mb-3">{service.title}</h3>
                <p className="text-gray-300">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Services */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl text-[#8B5CF6] mb-4" data-testid="ai-section">
              AI-POWERED MARKETING
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl">
              Harness the power of artificial intelligence to work smarter, faster, and more efficiently.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {aiServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-effect rounded-2xl p-8 hover:bg-white/10 transition-all"
              >
                <div className="w-16 h-16 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center mb-4 text-[#8B5CF6]">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-semibold text-white mb-3">{service.title}</h3>
                <p className="text-gray-300">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Services */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl text-[#06B6D4] mb-4" data-testid="tech-section">
              TECH & DEVELOPMENT
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl">
              Custom technology solutions that bring your vision to life and scale with your growth.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {techServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-effect rounded-2xl p-8 hover:bg-white/10 transition-all"
              >
                <div className="w-16 h-16 rounded-full bg-[#06B6D4]/20 flex items-center justify-center mb-4 text-[#06B6D4]">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-semibold text-white mb-3">{service.title}</h3>
                <p className="text-gray-300">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="glass-effect rounded-3xl p-12"
          >
            <h2 className="heading-font text-4xl text-white mb-6">
              NEED WORLD-CLASS VISUALS?
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              Explore Wake N' Bake Studio for cinematic production and brand films.
            </p>
            <Link
              to="/studio/services"
              className="inline-block px-8 py-4 bg-[#F97316] text-white rounded-full font-medium hover:bg-[#EA580C] transition-colors"
              data-testid="visit-studio-cta"
            >
              Visit Studio →
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer brand="brandify" />
    </div>
  );
};

const Target = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" strokeWidth="2"/>
    <circle cx="12" cy="12" r="6" strokeWidth="2"/>
    <circle cx="12" cy="12" r="2" strokeWidth="2"/>
  </svg>
);

export default BrandifyServices;